<style>
  /* bic-select zeigt bei onlyShowPlaceholder den Text standardmäßig grau (Placeholder-Optik) an.
     Wenn "Alle erlaubt" den tatsächlichen Auswahlwert darstellt, soll er wie ein normaler Wert wirken. */
  .force-value-color ::ng-deep input.text-gray-500 {
    color: #111827 !important;
  }
</style>
<div class="flex flex-col h-full">
  <app-page-header>
    <div class="flex flex-row items-center gap-4 flex-1">
      <button (click)="navigateBack()" class="text-gray-500 hover:text-gray-700">
        <fa-icon [icon]="faChevronLeft"></fa-icon>
      </button>
      <span class="text-xl font-medium">{{ ruleset?.name }}</span>
      @if (readOnly) {
        @if (linkedFromOtherProject) {
          <span tooltip="Dieser myCheck wurde aus einem anderen Projekt geteilt und kann nur dort bearbeitet werden."
                tooltipDirection="bottom">
            <bic-badge bgClass="bg-gray-100" textClass="text-gray-700">Geteilt · schreibgeschützt</bic-badge>
          </span>
        } @else {
          <bic-badge bgClass="bg-gray-100" textClass="text-gray-700">Bereitgestellt · schreibgeschützt</bic-badge>
        }
      }
    </div>
    @if (readOnly) {
      <bic-button (onClick)="navigateBack()" buttonType="white">Schließen</bic-button>
    } @else {
      @if (canRuleTest) {
        <!-- Testlauf (Figma „Testmodus" 01/06), Tooltip am Wrapper wegen des deaktivierten Buttons. -->
        <span class="relative inline-flex" [tooltip]="ruleTestTooltip" [tooltipSubtext]="ruleTestTooltipSubtext"
              tooltipDirection="bottom">
          <bic-button (onClick)="openRuleTest()" [disabled]="ruleTestLimitReached" buttonType="white">
            <fa-icon iconLeft [icon]="faPlay"></fa-icon>
            Testlauf
          </bic-button>
          @if (ruleTestUnseen) {
            <span class="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-green" aria-label="Neues Testergebnis"></span>
          }
        </span>
      }
      <bic-button (onClick)="navigateBack()" buttonType="white">Abbrechen</bic-button>
      <bic-button (onClick)="isDirty ? save() : navigateBack()">
        <fa-icon iconLeft [icon]="isDirty ? faFloppyDisk : faChevronLeft"></fa-icon>
        {{ isDirty ? 'Speichern' : 'Zurück' }}
      </bic-button>
    }
  </app-page-header>

  <!-- Vorschlags-Banner: volle Seitenbreite direkt unter der Topbar, weil sich die Zusammenfassung
       auf alle Sektionen bezieht, nicht nur auf den aktuell ausgewählten Tab im rechten Panel. -->
  <!-- Ist der Edit-Chat offen und zeigt dort die Review-Leiste, entfällt das Banner (#2789). -->
  @if (pendingProposal && hasOpenProposalChanges && !proposalReview.isShownInChat()) {
    <div class="px-6 pt-6">
      <app-proposal-banner
        (applyClicked)="onProposalApplyClicked()"
        (discardClicked)="onProposalDiscardClicked()"
        [proposal]="pendingProposal">
      </app-proposal-banner>
    </div>
  }

  <div class="flex flex-row flex-1 overflow-hidden gap-6 p-6">
    <!-- Unterhalb Full HD (1920px) schmaler, Labels truncaten dann bei Bedarf statt die Leiste
         immer auf voller Breite zu halten (siehe tailwind.config.js `full-hd`). -->
    <div class="flex flex-col gap-4 w-[240px] main-full-hd:w-[423px] shrink-0 overflow-y-auto">
      <button (click)="selectSection('general')"
              [class]="selectedSection === 'general' ? 'bg-gray-100 border-gray-300' : 'border-gray-200 hover:bg-gray-50'"
              [tooltip]="proposalNavTooltip(sectionNavStatus('general'))"
              class="group flex flex-row items-center gap-2 p-4 rounded-lg border text-left"
              tooltipColor="dark"
              tooltipDirection="right">
        <fa-icon [class]="selectedSection === 'general' ? 'text-gray-900' : 'text-gray-300'"
                 [icon]="faCircleInfo"
                 class="text-base group-hover:text-gray-900"></fa-icon>
        <span [class]="selectedSection === 'general' ? 'text-gray-900' : 'text-gray-300'"
              class="text-sm font-medium flex-1 truncate group-hover:text-gray-900">Allgemeine Angaben</span>
        <app-proposal-nav-marker [status]="sectionNavStatus('general')"></app-proposal-nav-marker>
      </button>

      <button (click)="selectSection('parameters')"
              [class]="selectedSection === 'parameters' ? 'bg-gray-100 border-gray-300' : 'border-gray-200 hover:bg-gray-50'"
              [tooltip]="proposalNavTooltip(sectionNavStatus('parameters'))"
              class="group flex flex-row items-center gap-2 p-4 rounded-lg border text-left"
              tooltipColor="dark"
              tooltipDirection="right">
        <fa-icon [class]="selectedSection === 'parameters' ? 'text-gray-900' : 'text-gray-300'"
                 [icon]="faGear"
                 class="text-base group-hover:text-gray-900"></fa-icon>
        <span [class]="selectedSection === 'parameters' ? 'text-gray-900' : 'text-gray-300'"
              class="text-sm font-medium flex-1 truncate group-hover:text-gray-900">Regelparameter</span>
        <app-proposal-nav-marker [status]="sectionNavStatus('parameters')"></app-proposal-nav-marker>
      </button>

      <button (click)="selectSection('output-format')"
              [class]="selectedSection === 'output-format' ? 'bg-gray-100 border-gray-300' : 'border-gray-200 hover:bg-gray-50'"
              [tooltip]="proposalNavTooltip(sectionNavStatus('output-format'))"
              class="group flex flex-row items-center gap-2 p-4 rounded-lg border text-left"
              tooltipColor="dark"
              tooltipDirection="right">
        <fa-icon [class]="selectedSection === 'output-format' ? 'text-gray-900' : 'text-gray-300'"
                 [icon]="faFile"
                 class="text-base group-hover:text-gray-900"></fa-icon>
        <span [class]="selectedSection === 'output-format' ? 'text-gray-900' : 'text-gray-300'"
              class="text-sm font-medium flex-1 truncate group-hover:text-gray-900">Ausgabeformat</span>
        <app-proposal-nav-marker [status]="sectionNavStatus('output-format')"></app-proposal-nav-marker>
      </button>

      <div class="flex flex-row items-center gap-2 pt-2">
        <span class="text-base font-medium text-gray-900 flex-1">Prüfregeln</span>
        <div [tooltip]="maxRulesTooltip" tooltipDirection="top">
          <bic-button (onClick)="createRule()" [disabled]="!canAddRule" buttonType="white">
            <fa-icon [icon]="faPlus"></fa-icon>
          </bic-button>
        </div>
      </div>

      @for (rule of sortedRules; track rule.id) {
        <div (click)="selectRule(rule)"
             [class]="selectedSection === 'rule' && selectedRuleId === rule.id ? 'bg-gray-100 border-gray-300' : 'border-gray-200 hover:bg-gray-50'"
             [tooltip]="proposalNavTooltip(ruleNavStatus(rule))"
             class="group flex flex-row items-center gap-2 p-4 rounded-lg border cursor-pointer"
             tooltipColor="dark"
             tooltipDirection="right">
          <fa-icon [class]="selectedSection === 'rule' && selectedRuleId === rule.id ? 'text-gray-900' : 'text-gray-300'"
                   [icon]="faCube"
                   class="text-base group-hover:text-gray-900"></fa-icon>
          <div class="flex flex-col flex-1 min-w-0">
            <span [class]="selectedSection === 'rule' && selectedRuleId === rule.id ? 'text-gray-900' : 'text-gray-300'"
                  class="text-sm font-medium truncate group-hover:text-gray-900">{{ getRuleListLabel(rule) }}</span>
          </div>
          <!-- Punkt rechts; bei Hover (bzw. offenem Menü) ersetzt ihn der Ellipsis-Trigger (Figma „RuleNav“). -->
          <app-proposal-nav-marker [hasMenu]="true" [menuOpen]="!!ruleMenu.dropdown?.isVisible()"
                                   [status]="ruleNavStatus(rule)">
            <div (click)="$event.stopPropagation()" [bicDropdownTrigger]="ruleMenu"
                 class="cursor-pointer flex items-center justify-center hover:opacity-50">
              <fa-icon [icon]="faEllipsisVertical"></fa-icon>
            </div>
          </app-proposal-nav-marker>
          <bic-dropdown #ruleMenu [dropdownPlacement]="'bottom-end'">
            <bic-dropdown-element (onClick)="duplicateRule(rule)">
              <div class="flex flex-row text-sm gap-3 text-gray-900 items-center">
                <fa-icon [icon]="faCopy" class="w-4"></fa-icon>
                <span>Duplizieren</span>
              </div>
            </bic-dropdown-element>
            <bic-dropdown-element (onClick)="onDeleteRule(rule)">
              <div class="flex flex-row text-sm gap-3 text-red-500 items-center">
                <fa-icon [icon]="faTrash" class="w-4"></fa-icon>
                <span>Löschen</span>
              </div>
            </bic-dropdown-element>
          </bic-dropdown>
        </div>
      }

      <!-- Regeln, die der aktuelle Vorschlag entfernen würde – Struktur-Guard verhindert das
           automatische Anwenden, dieser Block ist rein defensiv (siehe HybridDiff.ruleChanges.removed). -->
      @for (removedRule of removedProposalRules; track removedRule.id) {
        <div [attr.data-proposal-review]="'rule-removed:' + removedRule.id"
             [tooltip]="proposalNavTooltip('removed')"
             class="group flex flex-row items-center gap-2 p-4 rounded-lg border border-gray-200"
             tooltipColor="dark"
             tooltipDirection="right">
          <fa-icon [icon]="faCube" class="text-base text-gray-300 group-hover:text-gray-900"></fa-icon>
          <span class="text-sm font-medium flex-1 truncate text-gray-300 line-through group-hover:text-gray-900">{{ getRemovedRuleListLabel(removedRule) }}</span>
          <app-proposal-nav-marker status="removed"></app-proposal-nav-marker>
        </div>
      }
    </div>

    <div class="flex-1 min-w-0 border border-gray-200 rounded-lg overflow-y-auto">
      @if (selectedSection === 'general') {
        <div class="flex flex-col h-full min-h-0">
          <div class="flex flex-row items-center gap-4 px-4 py-2 border-b border-gray-200">
            <div class="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-full shrink-0">
              <fa-icon [icon]="faCircleInfo" class="text-base"></fa-icon>
            </div>
            <div class="flex flex-col">
              <span class="text-sm font-medium text-gray-900">Allgemeine Angaben</span>
              <span class="text-sm text-gray-500">Bezeichnung und Klassifikation</span>
            </div>
          </div>
          <div [formGroup]="form" class="flex flex-col gap-4 p-4">
            <app-change-marker (acceptClicked)="onFieldAccepted('name')" (rejectClicked)="onFieldRejected('name')"
                               [change]="$safeNavigationMigration(proposalDiff?.fieldChanges?.get('name'))"
                               label="myCheck Name" reviewKey="field:name">
              <bic-input formControlName="name" label="myCheck Name" placeholder="Name eingeben"></bic-input>
            </app-change-marker>
            <app-change-marker (acceptClicked)="onFieldAccepted('description')" (rejectClicked)="onFieldRejected('description')"
                               [change]="$safeNavigationMigration(proposalDiff?.fieldChanges?.get('description'))"
                               label="myCheck Beschreibung" reviewKey="field:description">
              <bic-input formControlName="description" label="myCheck Beschreibung"
                         placeholder="Beschreibung eingeben"></bic-input>
            </app-change-marker>
            <app-change-marker (acceptClicked)="onFieldAccepted('setConfig.allowedFileTypes')" (rejectClicked)="onFieldRejected('setConfig.allowedFileTypes')"
                               [change]="$safeNavigationMigration(proposalDiff?.fieldChanges?.get('setConfig.allowedFileTypes'))"
                               label="Erlaubte Dateitypen" reviewKey="field:setConfig.allowedFileTypes">
              <bic-select (valueChange)="updateAllowedFileTypes($event)"
                          [class.force-value-color]="areAllAllowedFileTypesSelected()"
                          [multiple]="true"
                          [onlyShowPlaceholder]="areAllAllowedFileTypesSelected()"
                          [placeholder]="areAllAllowedFileTypesSelected() ? 'Alle erlaubt' : 'Dateitypen auswählen'"
                          [value]="getAllowedFileTypesSelectionValue()"
                          formControlName="allowedFileTypes"
                          label="Erlaubte Dateitypen">
                <bic-dropdown-element
                  #allowedFileTypesSelectAllOption
                  (onClick)="toggleAllAllowedFileTypes()"
                  [selection]="true">{{ getAllowedFileTypesSelectAllLabel() }}
                </bic-dropdown-element>
                @for (fileType of availableFileTypes; track fileType) {
                  <bic-dropdown-element [selection]="true" [value]="fileType">{{ fileType }}</bic-dropdown-element>
                }
              </bic-select>
            </app-change-marker>
            <app-change-marker (acceptClicked)="onFieldAccepted('setConfig.allowedDocumentClasses')" (rejectClicked)="onFieldRejected('setConfig.allowedDocumentClasses')"
                               [change]="$safeNavigationMigration(proposalDiff?.fieldChanges?.get('setConfig.allowedDocumentClasses'))"
                               label="Erlaubte Dokumentenklassen" reviewKey="field:setConfig.allowedDocumentClasses">
              <bic-select (valueChange)="updateAllowedDocumentClasses($event)"
                          [class.force-value-color]="areAllAllowedDocumentClassesSelected()"
                          [multiple]="true"
                          [onlyShowPlaceholder]="areAllAllowedDocumentClassesSelected()"
                          [placeholder]="areAllAllowedDocumentClassesSelected() ? 'Alle erlaubt' : 'Dokumentenklassen auswählen'"
                          [value]="getAllowedDocumentClassesSelectionValue()"
                          formControlName="allowedDocumentClasses"
                          label="Erlaubte Dokumentenklassen">
                <bic-dropdown-element
                  #allowedDocumentClassesSelectAllOption
                  (onClick)="toggleAllAllowedDocumentClasses()"
                  [selection]="true">{{ getAllowedDocumentClassesSelectAllLabel() }}
                </bic-dropdown-element>
                @for (documentClass of availableDocumentClasses; track documentClass.id) {
                  <bic-dropdown-element [selection]="true" [value]="documentClass.id">{{ documentClass.name }}
                  </bic-dropdown-element>
                }
              </bic-select>
            </app-change-marker>
            <app-change-marker (acceptClicked)="onFieldAccepted('setConfig.checkMode')" (rejectClicked)="onFieldRejected('setConfig.checkMode')"
                               [change]="$safeNavigationMigration(proposalDiff?.fieldChanges?.get('setConfig.checkMode'))"
                               label="Prüfmodus" reviewKey="field:setConfig.checkMode">
              <div class="flex flex-col gap-1">
                <bic-select formControlName="checkMode" label="Prüfmodus" placeholder="Prüfmodus auswählen">
                  <bic-dropdown-element value="SINGLE">Jede Datei einzeln prüfen</bic-dropdown-element>
                  <bic-dropdown-element value="COMBINED">Alle Dateien zusammen prüfen</bic-dropdown-element>
                </bic-select>
                <!-- Ehrlicher Hinweis statt einer Option, die nichts tut: der gemeinsame Prüflauf ist
                     bislang nur für KI-Regelsets umgesetzt. Der hybride Pfad startet je Element eine
                     eigene Child-Instanz und braucht dafür einen eigenen Fan-in-Zustand. -->
                <span class="text-xs text-gray-500">„Alle Dateien zusammen prüfen" wirkt bei hybriden
                  Regelsets noch nicht – der Lauf prüft weiterhin jede Datei einzeln.</span>
              </div>
            </app-change-marker>
            <!-- KI-Modell: gilt für das gesamte Regelset und wird in die Prüfregeln synchronisiert -->
            <app-change-marker (acceptClicked)="onFieldAccepted('setConfig.kiModel')" (rejectClicked)="onFieldRejected('setConfig.kiModel')"
                               [change]="$safeNavigationMigration(proposalDiff?.fieldChanges?.get('setConfig.kiModel'))"
                               label="KI-Modell" reviewKey="field:setConfig.kiModel">
              <app-ai-model-field (valueChange)="onAiModelChange($event)" [value]="aiModel"/>
            </app-change-marker>
          </div>
        </div>
      }

      @if (selectedSection === 'parameters') {
        <div class="flex flex-col">
          <div class="flex flex-row items-center gap-4 px-4 py-2 border-b border-gray-200">
            <div class="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-full shrink-0">
              <fa-icon [icon]="faGear" class="text-base"></fa-icon>
            </div>
            <div class="flex flex-col">
              <span class="text-sm font-medium text-gray-900">Regelparameter</span>
              <span class="text-sm text-gray-500">Notwendige Daten und relevante Parameter zur Regelprüfung</span>
            </div>
          </div>
          <div class="flex flex-col gap-4 p-4 h-full min-h-0">
            <!-- Eingabeparameter (Set-Ebene): Prüfebene + Identifikator + Parameter-Tabelle -->
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium text-gray-900">Eingabeparameter</span>
              <span class="text-sm text-gray-500">Parameter zur Erfassung der Eingabedaten. Diese können in den
                Prüfregeln weiterverwendet werden.</span>
              @if (!eingabeparameterRequired) {
                <span class="text-sm text-gray-500">Bei reinen IFC-Regelsets nicht nötig: der Eingangsparameter
                  reicht die Datei direkt an die IFC-Bausteine weiter.</span>
              }
            </div>
            <div class="flex flex-row gap-4">
              <app-change-marker (acceptClicked)="onFieldAccepted('setConfig.pruefebene')" (rejectClicked)="onFieldRejected('setConfig.pruefebene')"
                                 [change]="$safeNavigationMigration(proposalDiff?.fieldChanges?.get('setConfig.pruefebene'))"
                               label="Prüfebene" reviewKey="field:setConfig.pruefebene" [rootClass]="'flex-1 min-w-0'">
                <div class="flex-1 min-w-0">
                  <bic-select (valueChange)="onPruefebeneChange($event)"
                              [value]="pruefebene"
                              label="Prüfebene"
                              placeholder="Prüfebene auswählen">
                    @for (option of pruefebeneOptions; track option.value) {
                      <bic-dropdown-element [value]="option.value">{{ option.label }}</bic-dropdown-element>
                    }
                  </bic-select>
                  <span class="text-xs text-gray-500">Ebene auf welcher geprüft werden soll. Beispielsweise Kapitel,
                    Seiten, LV-Positionen, Kostengruppen, etc.</span>
                </div>
              </app-change-marker>
              @if (pruefebene !== 'gesamtdokument') {
                <app-change-marker (acceptClicked)="onFieldAccepted('setConfig.identifikatorParamId')" (rejectClicked)="onFieldRejected('setConfig.identifikatorParamId')"
                                   [change]="$safeNavigationMigration(proposalDiff?.fieldChanges?.get('setConfig.identifikatorParamId'))"
                               label="Identifikator" reviewKey="field:setConfig.identifikatorParamId" [rootClass]="'w-[320px] shrink-0'">
                  <div class="w-[320px] shrink-0">
                    <bic-select (valueChange)="setIdentifikator($event)"
                                [disabled]="!identifikatorOptions.length"
                                [value]="identifikatorParamId"
                                label="Identifikator"
                                placeholder="Parameter auswählen">
                      @for (option of identifikatorOptions; track option.id) {
                        <bic-dropdown-element [value]="option.id">{{ option.name }}</bic-dropdown-element>
                      }
                    </bic-select>
                    <span class="text-xs text-gray-500">Parameter zur Identifizierung jeder Prüfeinheit.</span>
                  </div>
                </app-change-marker>
              }
            </div>
            @if (pruefebene !== 'gesamtdokument') {
              <app-change-marker (acceptClicked)="onFieldAccepted('setConfig.pruefebeneFilter')" (rejectClicked)="onFieldRejected('setConfig.pruefebeneFilter')"
                                 [change]="$safeNavigationMigration(proposalDiff?.fieldChanges?.get('setConfig.pruefebeneFilter'))"
                               label="Filter (optional)" reviewKey="field:setConfig.pruefebeneFilter">
                <div class="flex flex-col gap-1">
                  <bic-textarea [(value)]="pruefebeneFilter"
                                [rows]="2"
                                label="Filter (optional)"
                                placeholder="z. B. nur Kapitel zum Brandschutz, nur LV-Positionen zu Stahlbetonbauteilen, nur Elemente der Kostengruppe 300"></bic-textarea>
                  <span class="text-xs text-gray-500">KI-Anweisung, welche Einheiten dieser Prüfebene extrahiert
                    werden sollen. Leer lassen, um alle zu prüfen.</span>
                </div>
              </app-change-marker>
            }

            <div class="flex flex-col gap-4">
              <div [style.padding-right.px]="rowGutter('eingabeparameter')"
                   class="flex flex-row gap-4 text-xs font-medium text-gray-500 uppercase">
                  <span [tooltip]="'Bezeichnung'" class="flex-1 min-w-0 truncate">Bezeichnung</span>
                  <span [tooltip]="'Beschreibung'" class="flex-[2] min-w-0 truncate">Beschreibung</span>
                  <span [tooltip]="'Datentyp'" class="w-[200px] min-w-0 truncate">Datentyp</span>
                  <span class="w-[40px]"></span>
                </div>
              <ng-template #inputParamRow let-i="index" let-param let-removed="removed">
                  <div class="flex flex-row gap-4 items-center">
                    <app-change-marker [actions]="false" [change]="rowFieldChange('eingabeparameter', param, i, 'name')"
                                       rootClass="flex-1 min-w-0">
                      <bic-input [(value)]="param.name" [showX]="false" placeholder="Bezeichnung"></bic-input>
                    </app-change-marker>
                    <app-change-marker [actions]="false"
                                       [change]="rowFieldChange('eingabeparameter', param, i, 'beschreibung')"
                                       rootClass="flex-[2] min-w-0">
                      <bic-input [(value)]="param.beschreibung" [showX]="false"
                                 placeholder="Beschreibung"></bic-input>
                    </app-change-marker>
                    <app-change-marker [actions]="false"
                                       [change]="rowFieldChange('eingabeparameter', param, i, 'dataType')"
                                       rootClass="w-[200px] shrink-0">
                      <bic-select (valueChange)="updateEingabeparameterDataType(param, $event)"
                                  [disabled]="isIdentifikator(param)"
                                  [value]="param.dataType"
                                  placeholder="Datentyp">
                        @for (type of eingabeParameterTypes; track type) {
                          <bic-dropdown-element [value]="type">{{ type }}</bic-dropdown-element>
                        }
                      </bic-select>
                    </app-change-marker>
                    <!-- Optionen nur bei Datentyp „Auswahl": sie erzwingen zur Laufzeit das
                         Extraktionsergebnis (Enum im Antwortschema). -->
                    @if (needsOptions(param)) {
                      <app-change-marker [actions]="false"
                                         [change]="rowFieldChange('eingabeparameter', param, i, 'options')"
                                         rootClass="shrink-0">
                        <button (click)="openOptionsPopup('input', param)"
                                [class.border-gray-300]="optionsPreview(param).length"
                                [class.border-red]="!optionsPreview(param).length"
                                [class.text-gray-900]="optionsPreview(param).length"
                                [class.text-red]="!optionsPreview(param).length"
                                class="h-[38px] px-3 py-2 border rounded-lg bg-white inline-flex items-center gap-2 text-sm font-medium hover:bg-gray-50 transition"
                                data-proposal-mark>
                          <fa-icon [icon]="faPenToSquare" class="text-sm"></fa-icon>
                          {{ optionsPreview(param).length ? optionsPreview(param).join(' · ') : 'Optionen fehlen' }}
                        </button>
                      </app-change-marker>
                    }
                    @if (removed) {
                      <div class="w-[40px]"></div>
                    } @else {
                      <button (click)="removeEingabeparameter(i)"
                              class="w-[40px] flex justify-center text-gray-300 hover:text-gray-500">
                        <fa-icon [icon]="faTrash"></fa-icon>
                      </button>
                    }
                  </div>
              </ng-template>
              @for (param of eingabeparameter; track param.id) {
                <app-proposal-row (acceptClicked)="acceptRow('eingabeparameter', param)"
                                  (rejectClicked)="rejectRow('eingabeparameter', param)"
                                  [attr.data-proposal-review]="'row:eingabeparameter:' + $index"
                                  [pending]="rowPending('eingabeparameter', param)"
                                  [reserveSpace]="rowGutter('eingabeparameter') > 0">
                  <ng-container [ngTemplateOutletContext]="{$implicit: param, index: $index, removed: false}"
                                [ngTemplateOutlet]="inputParamRow"></ng-container>
                </app-proposal-row>
                }
              <!-- Vom Vorschlag entfernte Zeilen: ✓ bestätigt das Entfernen, ✗ stellt die Zeile wieder her. -->
              @for (entry of removedRows('eingabeparameter'); track entry) {
                <app-proposal-row (acceptClicked)="acceptRowRemoval('eingabeparameter', entry)"
                                  (rejectClicked)="rejectRowRemoval('eingabeparameter', entry)"
                                  [attr.data-proposal-review]="'row:eingabeparameter:removed:' + $index"
                                  [removed]="true" [reserveSpace]="true">
                  <ng-container [ngTemplateOutletContext]="{$implicit: entry.before, index: -1, removed: true}"
                                [ngTemplateOutlet]="inputParamRow"></ng-container>
                </app-proposal-row>
              }
            </div>

            <button
              (click)="addEingabeparameter()"
              class="flex flex-row items-center justify-center gap-2 py-2 text-sm font-medium text-gray-400 hover:text-gray-600">
              <fa-icon [icon]="faPlus"></fa-icon>
              Eingabeparameter hinzufügen
            </button>


            <div class="flex flex-col gap-4 pt-2">
                <div class="flex flex-col gap-1">
                  <span class="text-sm font-medium text-gray-900">Tags</span>
                  <span class="text-sm text-gray-500">Tags zur Organisation und Verwaltung der Prüfregel</span>
                </div>
              <div [style.padding-right.px]="rowGutter('tags')"
                   class="flex flex-row gap-4 text-xs font-medium text-gray-500 uppercase">
                  <span [tooltip]="'Bezeichnung'" class="flex-1 min-w-0 truncate">Bezeichnung</span>
                  <span [tooltip]="'Auswahl'" class="flex-1 min-w-0 truncate">Auswahl</span>
                  <span [tooltip]="'Notwendigkeit'" class="flex-1 min-w-0 truncate">Notwendigkeit</span>
                  <span class="w-[40px]"></span>
                </div>
              <ng-template #tagRow let-i="index" let-removed="removed" let-tag>
                  <div class="flex flex-row gap-4 items-center">
                    <app-change-marker [actions]="false" [change]="rowFieldChange('tags', tag, i, 'name')"
                                       rootClass="flex-1 min-w-0">
                      <bic-input [(value)]="tag.name" [showX]="false" placeholder="Bezeichnung"></bic-input>
                    </app-change-marker>
                    <div class="flex flex-row flex-1 gap-2 items-center">
                      <app-change-marker [actions]="false" [change]="rowFieldChange('tags', tag, i, 'options')"
                                         rootClass="flex-1 min-w-0">
                        <bic-select [disabled]="!tag.options?.length" placeholder="Auswahl treffen">
                          @for (opt of tag.options; track opt) {
                            <bic-dropdown-element [value]="opt">{{ opt }}</bic-dropdown-element>
                          }
                        </bic-select>
                      </app-change-marker>
                      <button (click)="openOptionsPopup('tag', tag)"
                              class="text-gray-900 hover:text-gray-600 transition shrink-0">
                        <fa-icon [icon]="faPenToSquare" class="text-base"></fa-icon>
                      </button>
                    </div>
                    <app-change-marker [actions]="false" [change]="rowFieldChange('tags', tag, i, 'necessity')"
                                       rootClass="flex-1 min-w-0">
                      <bic-select [(value)]="tag.necessity" placeholder="Notwendigkeit">
                        <bic-dropdown-element value="Pflicht">Pflicht</bic-dropdown-element>
                        <bic-dropdown-element value="Optional">Optional</bic-dropdown-element>
                      </bic-select>
                    </app-change-marker>
                    @if (removed) {
                      <div class="w-[40px]"></div>
                    } @else {
                      <button (click)="removeTag(i)"
                              class="w-[40px] flex justify-center text-gray-300 hover:text-gray-500">
                        <fa-icon [icon]="faTrash"></fa-icon>
                      </button>
                    }
                  </div>
              </ng-template>
              @for (tag of tags; track tag.id) {
                <app-proposal-row (acceptClicked)="acceptRow('tags', tag)" (rejectClicked)="rejectRow('tags', tag)"
                                  [attr.data-proposal-review]="'row:tags:' + $index"
                                  [pending]="rowPending('tags', tag)" [reserveSpace]="rowGutter('tags') > 0">
                  <ng-container [ngTemplateOutletContext]="{$implicit: tag, index: $index, removed: false}"
                                [ngTemplateOutlet]="tagRow"></ng-container>
                </app-proposal-row>
                }
              <!-- Vom Vorschlag entfernte Zeilen: ✓ bestätigt das Entfernen, ✗ stellt die Zeile wieder her. -->
              @for (entry of removedRows('tags'); track entry) {
                <app-proposal-row (acceptClicked)="acceptRowRemoval('tags', entry)"
                                  (rejectClicked)="rejectRowRemoval('tags', entry)"
                                  [attr.data-proposal-review]="'row:tags:removed:' + $index"
                                  [removed]="true" [reserveSpace]="true">
                  <ng-container [ngTemplateOutletContext]="{$implicit: entry.before, index: -1, removed: true}"
                                [ngTemplateOutlet]="tagRow"></ng-container>
                </app-proposal-row>
              }
            </div>

            <button
              (click)="addTag()"
              class="flex flex-row items-center justify-center gap-2 py-2 text-sm font-medium text-gray-400 hover:text-gray-600">
              <fa-icon [icon]="faPlus"></fa-icon>
              Tags hinzufügen
            </button>
          </div>
        </div>
      }

      @if (selectedSection === 'output-format') {
        <div class="flex flex-col">
          <div class="flex flex-row items-center gap-4 px-4 py-2 border-b border-gray-200">
            <div class="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-full shrink-0">
              <fa-icon [icon]="faFile" class="text-base"></fa-icon>
            </div>
            <div class="flex flex-col flex-1">
              <span class="text-sm font-medium text-gray-900">Ausgabeformat</span>
              <span class="text-sm text-gray-500">
                Konfiguration des Ausgabeformates der KI-generierten Antwort. Bei hybriden Prüfungen
                legt jede KI-Anweisung ihre Ausgabeparameter selbst fest – verbindlich ist der Knoten.
              </span>
            </div>
          </div>
          <div class="flex flex-col gap-4 p-4 flex-1 min-h-0">

            <div class="flex flex-col gap-4">
              <div [style.padding-right.px]="rowGutter('outputParameters')" class="flex flex-row items-center gap-4">
                  <span [tooltip]="'Modus'" class="w-[200px] text-xs font-medium text-gray-500 uppercase min-w-0 truncate">Modus</span>
                  <span [tooltip]="'Name'" class="flex-1 text-xs font-medium text-gray-500 uppercase min-w-0 truncate">Name</span>
                  <span [tooltip]="'Wert (Anweisung an die KI)'" class="flex-1 text-xs font-medium text-gray-500 uppercase min-w-0 truncate">Wert (Anweisung an die KI)</span>
                  <span [tooltip]="'Datentyp'" class="w-[200px] text-xs font-medium text-gray-500 uppercase min-w-0 truncate">Datentyp</span>
                  <span class="w-[40px]"></span>
                </div>
              <ng-template #outputRow let-i="index" let-param let-removed="removed">
                  <div class="flex flex-row items-center gap-4">
                    <app-change-marker [actions]="false"
                                       [change]="rowFieldChange('outputParameters', param, i, 'modus')"
                                       rootClass="w-[200px] shrink-0">
                      <bic-select [(value)]="param.modus" [disabled]="i < 3" placeholder="Modus">
                        @for (opt of modusOptions; track opt) {
                          <bic-dropdown-element [value]="opt">{{ opt }}</bic-dropdown-element>
                        }
                      </bic-select>
                    </app-change-marker>
                    <app-change-marker [actions]="false" [change]="rowFieldChange('outputParameters', param, i, 'name')"
                                       rootClass="flex-1 min-w-0">
                      <bic-input [(value)]="param.name" [disabled]="i < 3" [showX]="false"
                                 placeholder="Name"></bic-input>
                    </app-change-marker>
                    <app-change-marker [actions]="false" [change]="rowFieldChange('outputParameters', param, i, 'wert')"
                                       rootClass="flex-1 min-w-0">
                      <bic-input [(value)]="param.wert" [placeholder]="param.modus === 'Eigener Wert' ? 'Wert' : 'Wert (Anweisung an die KI)'"
                                 [showX]="false"></bic-input>
                    </app-change-marker>
                    <app-change-marker [actions]="false"
                                       [change]="rowFieldChange('outputParameters', param, i, 'dateityp')"
                                       rootClass="w-[200px] shrink-0">
                      <bic-select [(value)]="param.dateityp" placeholder="Datentyp">
                        @for (opt of dateitypOptions; track opt) {
                          <bic-dropdown-element [value]="opt">{{ opt }}</bic-dropdown-element>
                        }
                      </bic-select>
                    </app-change-marker>
                    @if (needsOptions(param)) {
                      <app-change-marker [actions]="false"
                                         [change]="rowFieldChange('outputParameters', param, i, 'options')"
                                         rootClass="shrink-0">
                        <button (click)="openOptionsPopup('output', param)"
                                [class.border-gray-300]="optionsPreview(param).length"
                                [class.border-red]="!optionsPreview(param).length"
                                [class.text-gray-900]="optionsPreview(param).length"
                                [class.text-red]="!optionsPreview(param).length"
                                class="h-[38px] px-3 py-2 border rounded-lg bg-white inline-flex items-center gap-2 text-sm font-medium hover:bg-gray-50 transition shrink-0"
                                data-proposal-mark>
                          <fa-icon [icon]="faPenToSquare" class="text-sm"></fa-icon>
                          {{ optionsPreview(param).length ? optionsPreview(param).join(' · ') : 'Optionen fehlen' }}
                        </button>
                      </app-change-marker>
                    }
                    @if (removed || i < 3) {
                      <div class="w-[40px]"></div>
                    } @else {
                      <button (click)="removeOutputParameter(i)"
                              class="w-[40px] flex justify-center text-gray-300 hover:text-gray-500">
                        <fa-icon [icon]="faTrash"></fa-icon>
                      </button>
                    }
                  </div>
              </ng-template>
              @for (param of outputParameters; track $index) {
                <app-proposal-row (acceptClicked)="acceptRow('outputParameters', param)"
                                  (rejectClicked)="rejectRow('outputParameters', param)"
                                  [attr.data-proposal-review]="'row:outputParameters:' + $index"
                                  [pending]="rowPending('outputParameters', param)"
                                  [reserveSpace]="rowGutter('outputParameters') > 0">
                  <ng-container [ngTemplateOutletContext]="{$implicit: param, index: $index, removed: false}"
                                [ngTemplateOutlet]="outputRow"></ng-container>
                </app-proposal-row>
                }
              <!-- Vom Vorschlag entfernte Zeilen: ✓ bestätigt das Entfernen, ✗ stellt die Zeile wieder her. -->
              @for (entry of removedRows('outputParameters'); track entry) {
                <app-proposal-row (acceptClicked)="acceptRowRemoval('outputParameters', entry)"
                                  (rejectClicked)="rejectRowRemoval('outputParameters', entry)"
                                  [attr.data-proposal-review]="'row:outputParameters:removed:' + $index"
                                  [removed]="true" [reserveSpace]="true">
                  <ng-container [ngTemplateOutletContext]="{$implicit: entry.before, index: -1, removed: true}"
                                [ngTemplateOutlet]="outputRow"></ng-container>
                </app-proposal-row>
              }
            </div>

            <button
              (click)="addOutputParameter()"
              class="flex flex-row items-center justify-center gap-2 py-2 text-sm font-medium text-gray-400 hover:text-gray-600">
              <fa-icon [icon]="faPlus"></fa-icon>
              Ausgabeparameter hinzufügen
            </button>
          </div>
        </div>
      }

      @if (selectedSection === 'rule' && selectedRule) {
        <div class="flex flex-col h-full">
          <div class="flex flex-row items-center gap-4 px-4 py-2 border-b border-gray-200">
            <div class="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-full shrink-0">
              <fa-icon [icon]="faTable" class="text-base"></fa-icon>
            </div>
            <div class="flex flex-col flex-1 min-w-0">
              <span class="text-sm font-medium text-gray-900 truncate">{{ selectedRule.name }}</span>
              <span class="text-sm text-gray-500">Hybride Prüfregel</span>
            </div>
            <div class="shrink-0">
              <bic-button (onClick)="openRuleEditor(selectedRule)" buttonType="white">
                <fa-icon [icon]="faChevronRight" iconRight></fa-icon>
                Workflow-Editor
              </bic-button>
            </div>
          </div>
          <div class="flex flex-col gap-4 p-4 h-full">
            <!-- items-end: Kopfzeile eines geänderten Felds ist höher als ein Label, die Felder bleiben unten bündig. -->
            <div class="grid gap-4 items-end" style="grid-template-columns: 1fr 4fr;">
              <app-change-marker (acceptClicked)="onRuleFieldAccepted(selectedRule, 'shortCode')" (rejectClicked)="onRuleFieldRejected(selectedRule, 'shortCode')"
                                 [change]="ruleFieldChange(selectedRule, 'shortCode')"
                                 [reviewKey]="'rule:' + selectedRule.id + ':shortCode'" label="Prüfregel Kürzel (max. 10 Zeichen)">
                <bic-input (valueChange)="onSelectedRuleShortCodeChange($event)"
                           [maxLength]="10"
                           [value]="selectedRuleShortCode"
                           placeholder="Kürzel eingeben"
                           label="Prüfregel Kürzel (max. 10 Zeichen)"></bic-input>
              </app-change-marker>
              <app-change-marker (acceptClicked)="onRuleFieldAccepted(selectedRule, 'name')" (rejectClicked)="onRuleFieldRejected(selectedRule, 'name')"
                                 [change]="ruleFieldChange(selectedRule, 'name')"
                                 [reviewKey]="'rule:' + selectedRule.id + ':name'" label="Prüfregelname">
                <bic-input (valueChange)="onSelectedRuleNameChange($event)"
                           [value]="selectedRule.name"
                           placeholder="Name eingeben"
                           label="Prüfregelname"></bic-input>
              </app-change-marker>
            </div>

            <div class="flex flex-col gap-4">
              <app-change-marker (acceptClicked)="onRuleFieldAccepted(selectedRule, 'description')" (rejectClicked)="onRuleFieldRejected(selectedRule, 'description')"
                                 [change]="ruleFieldChange(selectedRule, 'description')"
                                 [reviewKey]="'rule:' + selectedRule.id + ':description'" label="Prüfregelbeschreibung">
                <bic-input (valueChange)="onSelectedRuleDescriptionChange($event)"
                           [value]="selectedRule.description"
                           placeholder="Beschreibung eingeben"
                           label="Prüfregelbeschreibung"></bic-input>
              </app-change-marker>

              <!-- Regelparameter (auf Ruleset-Ebene definierte Tags) pro Regel ausfüllen -->
              @if (ruleParameterTags.length || unlistedRuleEntries(selectedRule, 'tagValues').length
                   || setRemovedRuleEntries(selectedRule, 'tagValues').length) {
                <!-- Entscheidung je Tag (#2789): jeder Tag-Wert hat seine eigene Kopfzeile mit ✓/✗. Vom
                     Vorschlag gelöschte Werte (alter Wert) und offene Werte ohne Tag im Set (aktueller Wert)
                     erscheinen nach den Tags schreibgeschützt. -->
                <div class="flex gap-4 items-end">
                  @for (tag of ruleParameterTags; track tag.id) {
                    @if (isRuleEntryListed(selectedRule, 'tagValues', tag.id)) {
                      <app-change-marker (acceptClicked)="onRuleEntryAccepted(selectedRule, 'tagValues', tag.id)"
                                         (rejectClicked)="onRuleEntryRejected(selectedRule, 'tagValues', tag.id)"
                                         [change]="ruleEntryChange(selectedRule, 'tagValues', tag.id)"
                                         [label]="tag.name"
                                         [reviewKey]="'rule:' + selectedRule.id + ':tagValues:' + tag.id"
                                         rootClass="flex-1 min-w-0">
                        <bic-select
                          (valueChange)="updateSelectedRuleTagValue(tag.id, $event)"
                          [disabled]="!tag.options?.length"
                          [label]="tag.name"
                          [value]="getSelectedRuleTagValue(tag.id)"
                          placeholder="Bitte wählen…">
                          @for (opt of tag.options; track opt) {
                            <bic-dropdown-element [value]="opt">{{ opt }}</bic-dropdown-element>
                          }
                        </bic-select>
                      </app-change-marker>
                    }
                  }
                  @for (entry of unlistedRuleEntries(selectedRule, 'tagValues'); track entry.key) {
                    <app-change-marker (acceptClicked)="onRuleEntryAccepted(selectedRule, 'tagValues', entry.key)"
                                       (rejectClicked)="onRuleEntryRejected(selectedRule, 'tagValues', entry.key)"
                                       [change]="entry.change"
                                       [label]="entry.name"
                                       [reviewKey]="'rule:' + selectedRule.id + ':tagValues:' + entry.key"
                                       rootClass="flex-1 min-w-0">
                      <bic-input [disabled]="true" [label]="entry.name" [showX]="false"
                                 [value]="entry.value"></bic-input>
                    </app-change-marker>
                  }
                  <!-- Auf Set-Ebene gelöscht, Regelwert unverändert: ✓/✗ sind an die Set-Zeile gekoppelt
                       (setRemovedRuleEntries) und gelten damit für alle Prüfregeln. -->
                  @for (entry of setRemovedRuleEntries(selectedRule, 'tagValues'); track entry.key) {
                    <app-change-marker (acceptClicked)="acceptRowRemoval('tags', entry.removal)"
                                       (rejectClicked)="rejectRowRemoval('tags', entry.removal)"
                                       [attr.data-proposal-set-removed]="'tagValues:' + entry.key"
                                       [change]="entry.change"
                                       [label]="entry.name"
                                       rootClass="flex-1 min-w-0">
                      <bic-input [disabled]="true" [label]="entry.name" [showX]="false"
                                 [value]="entry.value"></bic-input>
                    </app-change-marker>
                  }
                </div>
              }

              <div class="flex gap-4 items-end">
                <div class="w-1/2">
                  <app-change-marker (acceptClicked)="onRuleFieldAccepted(selectedRule, 'allowedFileTypes')" (rejectClicked)="onRuleFieldRejected(selectedRule, 'allowedFileTypes')"
                                     [change]="ruleFieldChange(selectedRule, 'allowedFileTypes')"
                                 [reviewKey]="'rule:' + selectedRule.id + ':allowedFileTypes'" label="Dateitypen">
                    <bic-select (valueChange)="updateSelectedRuleAllowedFileTypes($event)"
                                [class.force-value-color]="areAllSelectedRuleAllowedFileTypesSelected()"
                                [multiple]="true"
                                [onlyShowPlaceholder]="areAllSelectedRuleAllowedFileTypesSelected()"
                                [placeholder]="areAllSelectedRuleAllowedFileTypesSelected() ? 'Alle erlaubt' : 'Dateitypen auswählen'"
                                [value]="getSelectedRuleAllowedFileTypesSelectionValue()"
                                label="Dateitypen">
                      <bic-dropdown-element
                        #selectedRuleAllowedFileTypesSelectAllOption
                        (onClick)="toggleAllSelectedRuleAllowedFileTypes()"
                        [selection]="true">Alle erlauben
                      </bic-dropdown-element>
                      @for (fileType of getRuleAllowedFileTypeOptions(); track fileType) {
                        <bic-dropdown-element [selection]="true" [value]="fileType">{{ fileType }}
                        </bic-dropdown-element>
                      }
                    </bic-select>
                  </app-change-marker>
                </div>
                <div class="w-1/2">
                  <app-change-marker (acceptClicked)="onRuleFieldAccepted(selectedRule, 'allowedDocumentClasses')" (rejectClicked)="onRuleFieldRejected(selectedRule, 'allowedDocumentClasses')"
                                     [change]="ruleFieldChange(selectedRule, 'allowedDocumentClasses')"
                                 [reviewKey]="'rule:' + selectedRule.id + ':allowedDocumentClasses'" label="Dateiklassen">
                    <bic-select (valueChange)="updateSelectedRuleAllowedDocumentClasses($event)"
                                [class.force-value-color]="areAllSelectedRuleAllowedDocumentClassesSelected()"
                                [multiple]="true"
                                [onlyShowPlaceholder]="areAllSelectedRuleAllowedDocumentClassesSelected()"
                                [placeholder]="areAllSelectedRuleAllowedDocumentClassesSelected() ? 'Alle erlaubt' : 'Dateiklassen auswählen'"
                                [value]="getSelectedRuleAllowedDocumentClassesSelectionValue()"
                                label="Dateiklassen">
                      <bic-dropdown-element
                        #selectedRuleAllowedDocumentClassesSelectAllOption
                        (onClick)="toggleAllSelectedRuleAllowedDocumentClasses()"
                        [selection]="true">Alle erlauben
                      </bic-dropdown-element>
                      @for (documentClass of getRuleAllowedDocumentClassOptions(); track documentClass.id) {
                        <bic-dropdown-element [selection]="true" [value]="documentClass.id">{{ documentClass.name }}
                        </bic-dropdown-element>
                      }
                    </bic-select>
                  </app-change-marker>
                </div>
              </div>
              <span class="text-sm font-medium text-gray-900">Prüfregelübersicht</span>
            </div>

            <div class="grid grid-cols-3 gap-4 flex-1 min-h-0 items-stretch">
              <div class="border border-gray-200 rounded-lg flex flex-col overflow-hidden h-full">
                <div class="flex flex-row items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <div class="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-full shrink-0">
                    <fa-icon [icon]="faCube" class="text-base"></fa-icon>
                  </div>
                  <div class="flex flex-col min-w-0">
                    <span class="text-sm font-medium text-gray-900">Eingangsparameter</span>
                    <span class="text-xs text-gray-500">Im Regelset definierte Parameter</span>
                  </div>
                </div>
                <div class="px-4 flex-1">
                  @if (ruleVisibilityParameters.length === 0) {
                    <div class="py-3 text-sm text-gray-500">
                      Noch keine Eingabeparameter im Regelset definiert.
                    </div>
                  } @else if (overviewVisibleParameters.length === 0) {
                    <div class="py-3 text-sm text-gray-500">
                      Kein Eingangsparameter in dieser Regel aktiv.
                    </div>
                  } @else {
                    @for (param of overviewVisibleParameters; track param.id) {
                      <div [class.border-b]="!$last" [class.border-gray-200]="!$last"
                           class="py-3 flex flex-col gap-1">
                        <span class="text-sm text-gray-800 truncate">{{ param.name || 'Unbenannt' }}</span>
                        <div class="flex flex-row items-center gap-2">
                          <span
                            class="inline-flex items-center rounded-md px-2 py-0.5 bg-gray-200 text-xs font-medium text-gray-700">{{ param.dataType }}</span>
                          @if (isRuleParamLocked(param.id)) {
                            <span
                              class="inline-flex items-center rounded-md px-2 py-0.5 bg-blue-100 text-xs font-medium text-blue-700">Identifikator</span>
                          }
                        </div>
                      </div>
                    }
                  }
                  <div class="py-2 text-xs text-gray-400">
                    Welche Parameter diese Regel nutzt, steuerst du im Workflow-Editor am Eingangsparameter-Knoten.
                  </div>
                </div>
              </div>

              <div class="border border-gray-200 rounded-lg flex flex-col overflow-hidden h-full">
                <div class="flex flex-row items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <div class="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-full shrink-0">
                    <fa-icon [icon]="faListCheck" class="text-base"></fa-icon>
                  </div>
                  <div class="flex flex-col min-w-0">
                    <span class="text-sm font-medium text-gray-900">Prüfschritte</span>
                    <span class="text-xs text-gray-500">Was wird geprüft?</span>
                  </div>
                </div>
                <div class="px-4 flex-1">
                  @if (selectedRuleOverview.checkSteps.length === 0) {
                    <div class="py-3 text-sm text-gray-500">
                      Noch keine Prüfschritte vorhanden.
                    </div>
                  } @else {
                    @for (stepName of selectedRuleOverview.checkSteps; track stepName) {
                      <div [class.border-b]="!$last"
                           [class.border-gray-200]="!$last"
                           class="py-3 text-sm text-gray-900 flex items-center gap-4">
                        <span
                          class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 text-xs font-medium text-gray-700 shrink-0">{{ $index + 1 }}</span>
                        <span class="flex-1 min-w-0 whitespace-normal break-words">{{ stepName }}</span>
                      </div>
                    }
                  }
                </div>
              </div>

              <div class="border border-gray-200 rounded-lg flex flex-col overflow-hidden h-full">
                <div class="flex flex-row items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <div class="w-8 h-8 flex items-center justify-center bg-gray-200 rounded-full shrink-0">
                    <fa-icon [icon]="faMemoCircleCheck" class="text-base"></fa-icon>
                  </div>
                  <div class="flex flex-col min-w-0">
                    <span class="text-sm font-medium text-gray-900">Ergebnisse</span>
                    <span class="text-xs text-gray-500">Welche Ergebnisse sind möglich?</span>
                  </div>
                </div>
                <div class="px-4 flex-1">
                  @if (overviewResultItems.length === 0) {
                    <div class="py-3 text-sm text-gray-500">
                      Noch keine Ergebnisse definiert.
                    </div>
                  } @else {
                    @for (resultItem of overviewResultItems; track resultItem.label) {
                      <div [class.border-b]="!$last" [class.border-gray-200]="!$last" class="py-3">
                        <div class="flex flex-col gap-1 min-w-0">
                          <span
                            [class]="resultBadgeClasses(resultItem.tone)"
                            class="inline-flex items-center gap-1 rounded-md px-2.5 py-0.5 text-xs font-medium w-fit">{{ resultItem.label }}</span>
                          <span
                            class="text-sm font-medium text-gray-900 whitespace-normal break-words">{{ resultItem.description }}</span>
                        </div>
                      </div>
                    }
                  }
                  @if (selectedRuleOverview.resultScope) {
                    <div class="py-3 border-t border-gray-200">
                      <div class="flex flex-row items-center gap-2">
                        <span class="w-4 h-4 flex items-center justify-center shrink-0">
                          <fa-icon [icon]="faCircleInfo" class="text-gray-300 text-[16px] leading-none"></fa-icon>
                        </span>
                        <span class="h-4 flex items-center text-[14px] font-medium leading-none text-gray-500">{{ selectedRuleOverview.resultScope }}</span>
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  </div>
</div>

@if (optionsPopupVisible) {
  <app-options-popup
    (closed)="onOptionsPopupClosed()"
    (saved)="onOptionsPopupSaved($event)"
    [initialOptions]="optionsPopupOptions"
    [label]="optionsPopupLabel">
  </app-options-popup>
}

@if (ruleToDelete) {
  <bic-popup (close)="cancelDelete()" [hasBackdrop]="true" customClass="w-[560px]"
             title="Prüfregel löschen?">
    <div bicPopupContent class="flex flex-col gap-4">
      <span class="font-normal">Möchten Sie die Prüfregel „{{ ruleToDelete.name }}" wirklich löschen?<br><br>
        Diese Aktion kann nicht rückgängig gemacht werden.</span>
      <app-rule-usage-warning [loading]="deleteUsagesLoading" [usages]="deleteUsages"></app-rule-usage-warning>
    </div>
    <div bicPopupButtonGroup class="flex flex-row w-full justify-end items-center gap-4">
      <bic-button (onClick)="cancelDelete()" buttonType="white">Abbrechen</bic-button>
      <bic-button (onClick)="confirmDelete()" [disabled]="deleteUsagesLoading" buttonType="primary">Löschen</bic-button>
    </div>
  </bic-popup>
}

@if (ruleTestContext) {
  <app-rule-test-popup [context]="ruleTestContext" (closed)="closeRuleTest()"></app-rule-test-popup>
}
